import { useEffect, useState, useCallback } from 'react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreditCard, Landmark, Save, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';

export default function AdminPaymentGateways() {
    const [razorpay, setRazorpay] = useState({ enabled: true, key_id: '', key_secret: '' });
    const [pinelabs, setPinelabs] = useState({ enabled: false, client_id: '', client_secret: '', webhook_secret: '', environment: 'uat' });
    const [indusind, setIndusind] = useState({ enabled: false, merchant_id: '', access_code: '', working_key: '' });
    const [secretSet, setSecretSet] = useState({ razorpay: false, indusind: false, pinelabs_client: false, pinelabs_webhook: false });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get('/admin/payment-gateways');
        setRazorpay({ enabled: data.razorpay.enabled, key_id: data.razorpay.key_id || '', key_secret: '' });
        setPinelabs({
            enabled: data.pinelabs?.enabled ?? false,
            client_id: data.pinelabs?.client_id || '',
            environment: data.pinelabs?.environment || 'uat',
            client_secret: '',
            webhook_secret: '',
        });
        setIndusind({
            enabled: data.indusind.enabled,
            merchant_id: data.indusind.merchant_id || '',
            access_code: data.indusind.access_code || '',
            working_key: '',
        });
        setSecretSet({
            razorpay: !!data.razorpay.key_secret_set,
            indusind: !!data.indusind.working_key_set,
            pinelabs_client: !!data.pinelabs?.client_secret_set,
            pinelabs_webhook: !!data.pinelabs?.webhook_secret_set,
        });
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setLoading(true);
        try {
            await api.put('/admin/payment-gateways', { razorpay, pinelabs, indusind });
            toast.success('Payment gateway settings saved');
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminShell title="Payment Gateways">
            <div className="max-w-3xl space-y-6" data-testid="admin-payment-gateways-page">
                <p className="text-sm text-muted-foreground">
                    Enable the gateways you want to offer at checkout and enter their credentials. Customers can choose from the enabled gateways. Disabled gateways are hidden from the payment page. All checkouts are <strong>UPI-only</strong>.
                </p>

                {/* Razorpay */}
                <Card className="p-6 space-y-4" data-testid="gateway-razorpay-card">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-lg bg-[#EFF6FF] text-[#2563EB] flex items-center justify-center"><CreditCard className="h-4 w-4" /></div>
                            <div>
                                <div className="font-display font-semibold">Razorpay <span className="text-xs font-normal text-muted-foreground">· UPI only</span></div>
                                <div className="text-xs text-emerald-600 font-medium">Active — fully integrated</div>
                            </div>
                        </div>
                        <Switch checked={razorpay.enabled} onCheckedChange={(v) => setRazorpay({ ...razorpay, enabled: v })} data-testid="razorpay-enabled-switch" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Key ID</Label>
                            <Input value={razorpay.key_id} onChange={(e) => setRazorpay({ ...razorpay, key_id: e.target.value })} placeholder="rzp_live_… / rzp_test_…" data-testid="razorpay-key-id" />
                        </div>
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Key Secret</Label>
                            <Input type="password" value={razorpay.key_secret} onChange={(e) => setRazorpay({ ...razorpay, key_secret: e.target.value })} placeholder={secretSet.razorpay ? '•••••••• saved — leave blank to keep' : 'Enter Razorpay key secret'} data-testid="razorpay-key-secret" />
                        </div>
                    </div>
                </Card>

                {/* Pine Labs */}
                <Card className="p-6 space-y-4" data-testid="gateway-pinelabs-card">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-lg bg-[#ECFDF5] text-[#047857] flex items-center justify-center"><Smartphone className="h-4 w-4" /></div>
                            <div>
                                <div className="font-display font-semibold">Pine Labs (Plural) <span className="text-xs font-normal text-muted-foreground">· UPI only</span></div>
                                <div className="text-xs text-emerald-600 font-medium">Active — hosted UPI checkout</div>
                            </div>
                        </div>
                        <Switch checked={pinelabs.enabled} onCheckedChange={(v) => setPinelabs({ ...pinelabs, enabled: v })} data-testid="pinelabs-enabled-switch" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client ID</Label>
                            <Input value={pinelabs.client_id} onChange={(e) => setPinelabs({ ...pinelabs, client_id: e.target.value })} placeholder="Pine Labs Client ID" data-testid="pinelabs-client-id" />
                        </div>
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Environment</Label>
                            <Select value={pinelabs.environment} onValueChange={(v) => setPinelabs({ ...pinelabs, environment: v })}>
                                <SelectTrigger data-testid="pinelabs-environment"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="uat">UAT (sandbox / testing)</SelectItem>
                                    <SelectItem value="production">Production (live)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client Secret</Label>
                            <Input type="password" value={pinelabs.client_secret} onChange={(e) => setPinelabs({ ...pinelabs, client_secret: e.target.value })} placeholder={secretSet.pinelabs_client ? '•••••••• saved — leave blank to keep' : 'Enter Client Secret'} data-testid="pinelabs-client-secret" />
                        </div>
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Webhook Secret</Label>
                            <Input type="password" value={pinelabs.webhook_secret} onChange={(e) => setPinelabs({ ...pinelabs, webhook_secret: e.target.value })} placeholder={secretSet.pinelabs_webhook ? '•••••••• saved — leave blank to keep' : 'Enter Webhook Secret'} data-testid="pinelabs-webhook-secret" />
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Enable this only after entering valid credentials. Set your Pine Labs dashboard webhook URL to
                        <code className="mx-1 px-1 rounded bg-secondary">{`{your-domain}`}/api/recharge/pinelabs/webhook</code>.
                    </p>
                </Card>

                {/* IndusInd */}
                <Card className="p-6 space-y-4" data-testid="gateway-indusind-card">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-lg bg-[#FEF3F2] text-[#B42318] flex items-center justify-center"><Landmark className="h-4 w-4" /></div>
                            <div>
                                <div className="font-display font-semibold">IndusInd</div>
                                <div className="text-xs text-amber-600 font-medium">Credentials only — payment processing coming soon</div>
                            </div>
                        </div>
                        <Switch checked={indusind.enabled} onCheckedChange={(v) => setIndusind({ ...indusind, enabled: v })} data-testid="indusind-enabled-switch" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Merchant ID</Label>
                            <Input value={indusind.merchant_id} onChange={(e) => setIndusind({ ...indusind, merchant_id: e.target.value })} data-testid="indusind-merchant-id" />
                        </div>
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Access Code</Label>
                            <Input value={indusind.access_code} onChange={(e) => setIndusind({ ...indusind, access_code: e.target.value })} data-testid="indusind-access-code" />
                        </div>
                        <div className="grid gap-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Working Key</Label>
                            <Input type="password" value={indusind.working_key} onChange={(e) => setIndusind({ ...indusind, working_key: e.target.value })} placeholder={secretSet.indusind ? '•••••••• saved — leave blank to keep' : 'Enter working key'} data-testid="indusind-working-key" />
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Note: IndusInd credentials are stored securely now. Checkout will start routing payments to IndusInd once its API is wired up.
                    </p>
                </Card>

                <Button onClick={save} disabled={loading} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="save-gateways-button">
                    <Save className="h-4 w-4 mr-2" /> {loading ? 'Saving…' : 'Save settings'}
                </Button>
            </div>
        </AdminShell>
    );
}
